/**
 * Análisis de crédito: capacidad de pago del flujo familiar-negocio y
 * cobertura de garantías frente al monto solicitado.
 */
import { keyedEnum } from '../catalogs/enum.js';
import { BASIS_POINTS_PER_UNIT, divideRounded } from '../money/money.js';
/** Niveles de riesgo de la capacidad de pago. */
export const RISK_LEVELS = ['low', 'medium', 'high'];
/** Catálogo con clave de niveles de riesgo: `RISK_LEVEL.low`. */
export const RISK_LEVEL = keyedEnum(RISK_LEVELS);
/** Etiquetas en español de cada nivel de riesgo. */
export const RISK_LEVEL_LABELS = {
    low: 'Bajo',
    medium: 'Medio',
    high: 'Alto',
};
/** Meses en un año, para llevar cuotas de cualquier frecuencia a su equivalente mensual. */
const MONTHS_PER_YEAR = 12;
/**
 * Relación cuota/excedente que se registra cuando el flujo neto es cero o
 * negativo: un valor imposible (999.99 %) que siempre excede cualquier tope.
 */
const NO_CAPACITY_RATIO = 99_999;
/** Tope por defecto de la relación cuota/excedente: 40 %. */
export const DEFAULT_MAX_INSTALLMENT_TO_INCOME_BP = 4000;
/** Relación cuota/excedente por debajo de la cual el riesgo es bajo: 25 %. */
export const DEFAULT_LOW_RISK_THRESHOLD_BP = 2500;
/** Política de capacidad por defecto (práctica usual en microfinanzas guatemaltecas). */
export const DEFAULT_CAPACITY_POLICY = {
    maxInstallmentToIncome: DEFAULT_MAX_INSTALLMENT_TO_INCOME_BP,
    lowRiskThreshold: DEFAULT_LOW_RISK_THRESHOLD_BP,
};
/** Convierte una cuota de cualquier frecuencia a su equivalente mensual. */
export function toMonthlyAmount(installment, periodsPerYear) {
    return divideRounded(installment * periodsPerYear, MONTHS_PER_YEAR);
}
/**
 * Capacidad de pago: ¿puede el flujo neto mensual del negocio y el hogar
 * sostener la nueva cuota? La razón es la clásica "relación cuota/excedente"
 * que usan los analistas de microfinanzas en Guatemala.
 */
export function assessPaymentCapacity(flow, monthlyInstallment, policy = DEFAULT_CAPACITY_POLICY) {
    const netAvailable = (flow.businessIncome +
        flow.otherIncome -
        flow.businessExpenses -
        flow.householdExpenses -
        flow.existingDebtPayments);
    const installmentToIncome = (netAvailable > 0 ? divideRounded(monthlyInstallment * BASIS_POINTS_PER_UNIT, netAvailable) : NO_CAPACITY_RATIO);
    const isSufficient = installmentToIncome <= policy.maxInstallmentToIncome;
    const riskLevel = installmentToIncome <= policy.lowRiskThreshold ? RISK_LEVEL.low : isSufficient ? RISK_LEVEL.medium : RISK_LEVEL.high;
    return {
        netAvailable,
        monthlyInstallment,
        installmentToIncome,
        surplusAfterInstallment: (netAvailable - monthlyInstallment),
        isSufficient,
        riskLevel,
    };
}
/**
 * Una cuota es "pico" cuando supera en más de 50 % la cuota con que se
 * evaluó la capacidad (15000 = 150 %): típico del capital al vencimiento, el
 * pago único y los planes estacionales.
 */
export const PEAK_INSTALLMENT_FACTOR_BP = 15_000;
/** Resultado de revisar la cuota pico contra el ingreso de la temporada. */
export const PEAK_CHECK_STATUSES = ['not_applicable', 'covered', 'not_covered', 'income_missing'];
/** Catálogo con clave de estados de la revisión de la cuota pico. */
export const PEAK_CHECK_STATUS = keyedEnum(PEAK_CHECK_STATUSES);
/** Explicación en español de cada estado, para el analista y el comité. */
export const PEAK_CHECK_STATUS_LABELS = {
    not_applicable: 'Cuotas parejas: no hay cuota pico',
    covered: 'El ingreso de la temporada cubre la cuota más alta',
    not_covered: 'El ingreso de la temporada no alcanza para la cuota más alta',
    income_missing: 'Falta el ingreso esperado en la temporada de la cuota más alta',
};
/**
 * Revisa la cuota más alta del plan cuando es mucho mayor que la cuota con
 * que se evaluó la capacidad. El promedio mensual no basta en un crédito a
 * cosecha: el cliente debe tener, en ese período, un ingreso neto que cubra
 * la cuota grande dentro de la misma política (cuota/excedente).
 *
 * @param peakPeriodIncome Ingreso neto esperado en el período de la cuota
 *   más alta (cosecha, temporada), o `null` si el analista no lo registró.
 */
export function assessPeakInstallment(schedule, evaluatedInstallment, peakPeriodIncome, policy = DEFAULT_CAPACITY_POLICY) {
    const peak = schedule.reduce((highest, row) => (!highest || row.total > highest.total ? row : highest), null);
    const peakInstallment = (peak?.total ?? 0);
    const base = { peakInstallment, peakDueDate: peak?.dueDate ?? null };
    const isPeak = peakInstallment * BASIS_POINTS_PER_UNIT > evaluatedInstallment * PEAK_INSTALLMENT_FACTOR_BP;
    if (!isPeak)
        return { ...base, status: PEAK_CHECK_STATUS.not_applicable, peakToIncome: null };
    if (peakPeriodIncome === null)
        return { ...base, status: PEAK_CHECK_STATUS.income_missing, peakToIncome: null };
    const peakToIncome = (peakPeriodIncome > 0 ? divideRounded(peakInstallment * BASIS_POINTS_PER_UNIT, peakPeriodIncome) : NO_CAPACITY_RATIO);
    const status = peakToIncome <= policy.maxInstallmentToIncome ? PEAK_CHECK_STATUS.covered : PEAK_CHECK_STATUS.not_covered;
    return { ...base, status, peakToIncome };
}
/**
 * Capacidad final: una cuota pico sin ingreso de temporada que la respalde
 * vuelve el riesgo alto aunque el promedio mensual se vea bien.
 */
export function withPeakCheck(capacity, peak) {
    const peakFails = peak.status === PEAK_CHECK_STATUS.not_covered || peak.status === PEAK_CHECK_STATUS.income_missing;
    return {
        ...capacity,
        isSufficient: capacity.isSufficient && peak.status !== PEAK_CHECK_STATUS.not_covered,
        riskLevel: peakFails ? RISK_LEVEL.high : capacity.riskLevel,
        peak,
    };
}
/**
 * Cuota más alta que cabe en la política de capacidad: base del "monto
 * máximo sugerido" que ve el asesor mientras estructura el préstamo.
 */
export function maxAffordableInstallment(netAvailable, policy = DEFAULT_CAPACITY_POLICY) {
    if (netAvailable <= 0)
        return 0;
    return Math.floor((netAvailable * policy.maxInstallmentToIncome) / BASIS_POINTS_PER_UNIT);
}
/** Garantía vs. monto: la cobertura usa el valor de realización cuando existe (criterio conservador). */
export function collateralCoverage(collaterals, principal, requiredCoverage) {
    const coverageValue = collaterals.reduce((sum, c) => sum + (c.forcedSaleValue ?? c.marketValue), 0);
    const coverage = (principal > 0 ? divideRounded(coverageValue * BASIS_POINTS_PER_UNIT, principal) : 0);
    const required = divideRounded(principal * requiredCoverage, BASIS_POINTS_PER_UNIT);
    return {
        coverageValue,
        coverage,
        isCovered: coverageValue >= required,
        shortfall: Math.max(0, required - coverageValue),
    };
}
//# sourceMappingURL=underwriting.js.map